import { subscribeSseJsonEvents } from './sseEventStream'

/** SSE 断线后列表同步 / 单任务兜底的 HTTP 轮询间隔（毫秒） */
export const SSE_FALLBACK_POLL_MS = 8000

/** SSE 断线后的主动轮询间隔（毫秒） */
export const SSE_LEGACY_POLL_MS = 1500

/** 等待 Hub 推送时的内存检查间隔（毫秒，非 HTTP） */
export const SSE_PUSH_TICK_MS = 200

/** SSE 连接状态探测间隔（毫秒，非 HTTP） */
export const SSE_LINK_WATCH_MS = 1000

/**
 * 阻塞直到收到 Hub 推送或取消；SSE 正常时专用，不发 HTTP 请求。
 */
export async function awaitHubPush<T>(
  getPending: () => T | null,
  clearPending: () => void,
  options?: { tickMs?: number; isCancelled?: () => boolean },
): Promise<T | null> {
  const tickMs = options?.tickMs ?? SSE_PUSH_TICK_MS
  while (true) {
    if (options?.isCancelled?.()) return null
    const pending = getPending()
    if (pending) {
      clearPending()
      return pending
    }
    await new Promise((r) => setTimeout(r, tickMs))
  }
}

/**
 * 在 deadline 前轮询 pending 任务；收到推送则消费并返回，超时返回 null。
 * 供 SSE 断线时「限时等待推送，否则触发 HTTP 兜底」的场景使用。
 */
export async function awaitPendingHubTask<T>(
  getPending: () => T | null,
  clearPending: () => void,
  deadlineMs: number,
  options?: { tickMs?: number; isCancelled?: () => boolean },
): Promise<T | null> {
  const tickMs = options?.tickMs ?? SSE_PUSH_TICK_MS
  while (Date.now() < deadlineMs) {
    if (options?.isCancelled?.()) return null
    const pending = getPending()
    if (pending) {
      clearPending()
      return pending
    }
    await new Promise((r) => setTimeout(r, tickMs))
  }
  return null
}

export type AsyncTaskListener<T> = (task: T) => void

export type AsyncTaskHub<T> = {
  dispatch: (task: T) => void
  isSseLive: () => boolean
  subscribe: (taskId: string, listener: AsyncTaskListener<T>) => () => void
  waitForTerminal: (initial: T, options: WaitForAsyncTaskTerminalOptions<T>) => Promise<T>
}

export type CreateAsyncTaskHubOptions<T> = {
  path: string
  eventName: string
  getId: (task: T) => string
}

export type WaitForAsyncTaskTerminalOptions<T> = {
  isTerminal: (task: T) => boolean
  load: (taskId: string) => Promise<T>
  maxWaitMs: number
  pollMs: number
  timeoutMessage: string
}

export function createAsyncTaskHub<T>(
  options: CreateAsyncTaskHubOptions<T>,
): AsyncTaskHub<T> {
  let hubRefCount = 0
  let stopHub: (() => void) | null = null
  let sseLive = true
  const listenersByTaskId = new Map<string, Set<AsyncTaskListener<T>>>()
  const latestByTaskId = new Map<string, T>()

  const dispatch = (task: T): void => {
    const taskId = options.getId(task)
    latestByTaskId.set(taskId, task)
    const listeners = listenersByTaskId.get(taskId)
    if (!listeners) return
    for (const listener of listeners) {
      listener(task)
    }
  }

  const ensureHub = (): void => {
    if (stopHub) return
    sseLive = true
    stopHub = subscribeSseJsonEvents<T>({
      path: options.path,
      eventName: options.eventName,
      onPayload: dispatch,
      onDisconnected: () => {
        sseLive = false
        stopHub = null
        if (hubRefCount > 0) {
          ensureHub()
        }
      },
    })
  }

  const subscribe = (taskId: string, listener: AsyncTaskListener<T>): (() => void) => {
    hubRefCount += 1
    ensureHub()
    const bucket = listenersByTaskId.get(taskId) ?? new Set<AsyncTaskListener<T>>()
    bucket.add(listener)
    listenersByTaskId.set(taskId, bucket)
    const cached = latestByTaskId.get(taskId)
    if (cached) {
      listener(cached)
    }
    return () => {
      bucket.delete(listener)
      if (bucket.size === 0) {
        listenersByTaskId.delete(taskId)
      }
      hubRefCount -= 1
      if (hubRefCount <= 0 && stopHub) {
        stopHub()
        stopHub = null
        sseLive = true
      }
    }
  }

  const waitForTerminal = (
    initial: T,
    waitOptions: WaitForAsyncTaskTerminalOptions<T>,
  ): Promise<T> => {
    let task = initial
    if (waitOptions.isTerminal(task)) {
      return Promise.resolve(task)
    }

    return new Promise<T>((resolve, reject) => {
      let settled = false
      let pollTimer: ReturnType<typeof setTimeout> | null = null
      let linkWatchTimer: ReturnType<typeof setTimeout> | null = null
      let unsubscribe: () => void = () => {}

      const clearTimers = () => {
        if (pollTimer) clearTimeout(pollTimer)
        if (linkWatchTimer) clearTimeout(linkWatchTimer)
        pollTimer = null
        linkWatchTimer = null
      }

      const finish = (next: T) => {
        if (settled) return
        settled = true
        clearTimers()
        unsubscribe()
        resolve(next)
      }

      const fail = (message: string) => {
        if (settled) return
        settled = true
        clearTimers()
        unsubscribe()
        reject(new Error(message))
      }

      const deadlineTimer = setTimeout(() => {
        fail(waitOptions.timeoutMessage)
      }, waitOptions.maxWaitMs)

      const wrapFinish = (next: T) => {
        clearTimeout(deadlineTimer)
        finish(next)
      }

      unsubscribe = subscribe(options.getId(task), (incoming) => {
        task = incoming
        if (waitOptions.isTerminal(task)) {
          wrapFinish(task)
        }
      })
      if (settled) {
        clearTimeout(deadlineTimer)
        unsubscribe()
        return
      }

      const runHttpPoll = async () => {
        if (settled) return
        if (sseLive) {
          watchLink()
          return
        }
        try {
          const fresh = await waitOptions.load(options.getId(task))
          task = fresh
          dispatch(fresh)
          if (waitOptions.isTerminal(task)) {
            wrapFinish(task)
            return
          }
        } catch {
          /* 单次查询失败继续等待 */
        }
        if (!settled) {
          pollTimer = setTimeout(() => void runHttpPoll(), waitOptions.pollMs)
        }
      }

      const watchLink = () => {
        if (settled) return
        if (!sseLive) {
          void runHttpPoll()
          return
        }
        linkWatchTimer = setTimeout(watchLink, SSE_LINK_WATCH_MS)
      }

      watchLink()
    })
  }

  return {
    dispatch,
    isSseLive: () => sseLive,
    subscribe,
    waitForTerminal,
  }
}
